// Browser-only PDF export for a Pro plan.
// Each day gets its OWN complete route: its own static map, its own numbered
// markers (1..N restarting every day), its own stop list and daily summary.
// Uses only data already stored in the plan — no AI calls, no credits, no
// itinerary mutation. Coordinates are never invented.
import { exportItineraryPdf, type PdfDaySection, type PdfItem } from "@/lib/itinerary-pdf";
import { getStaticRouteMapDataUrl } from "@/lib/maps.functions";
import {
  buildDaySections,
  dayRouteUrl,
  formatDistance,
  formatDuration,
  type DayPlanEntry,
} from "@/lib/pdf-day-plan";
import { parseNegativePreferences } from "@/lib/negative-preferences";
import { getMobilityResources } from "@/lib/mobility/mobility.functions";
import type { MobilityGroup } from "@/lib/mobility/teaser";
import {
  buildPdfMobilitySection,
  relevantGroupsForTransport,
  type PdfPickupPoint,
} from "@/lib/pdf-mobility";
import type { ProPlan, ProDayStop } from "./pipeline.functions";

/**
 * Secção "Mobilidade oficial" do PDF. Lê apenas o diretório curado (sem IA,
 * sem Maps) e nunca inventa links. `tier` decide se os links são incluídos.
 */
async function buildMobilitySection(plan: ProPlan, tier: "pro" | "free") {
  const cityNames = Array.from(new Set(plan.days.map((d) => d.city))).slice(0, 3);
  const cities: Array<{ city: string; groups: MobilityGroup[] }> = [];
  for (const city of cityNames) {
    try {
      const res = await getMobilityResources({ data: { city, transport: plan.transport } });
      cities.push({
        city,
        groups: relevantGroupsForTransport(res.groups as MobilityGroup[], plan.transport),
      });
    } catch {
      // Sem recursos: o PDF sai na mesma, apenas sem esta secção.
    }
  }
  // Locais concretos já validados no itinerário (ex.: parque de estacionamento).
  const pickupPoints: PdfPickupPoint[] = [];
  for (const day of plan.days) {
    for (const s of day.stops as ProDayStop[]) {
      if (s.kind !== "parking" || s.lat == null || s.lng == null) continue;
      pickupPoints.push({
        city: day.city,
        mode: "parking",
        name: s.name.replace(/^🅿️\s*/, ""),
        mapsUrl: mapsUrlFor(s)!,
      });
    }
  }
  return buildPdfMobilitySection({ tier, cities, pickupPoints });
}

function mapsUrlFor(s: { lat?: number | null; lng?: number | null; placeId?: string | null }) {
  if (!s.lat || !s.lng) return undefined;
  const params = new URLSearchParams({ api: "1", query: `${s.lat},${s.lng}` });
  if (s.placeId) params.set("query_place_id", s.placeId);
  return `https://www.google.com/maps/search/?${params.toString()}`;
}

function endTime(startHHMM: string, mins: number): string | undefined {
  if (!/^\d{2}:\d{2}$/.test(startHHMM)) return undefined;
  const [h, m] = startHHMM.split(":").map(Number);
  const t = h * 60 + m + mins;
  const nh = Math.floor(t / 60) % 24;
  const nm = t % 60;
  return `${String(nh).padStart(2, "0")}:${String(nm).padStart(2, "0")}`;
}

const TRANSPORT_LABEL: Record<string, string> = {
  walk: "A pé",
  bike: "Bicicleta",
  car: "Carro",
  transit: "Transportes públicos",
};

const TRAVEL_MODE: Record<string, "walking" | "driving" | "transit" | "bicycling"> = {
  walk: "walking",
  car: "driving",
  transit: "transit",
  bike: "bicycling",
};

export async function exportProPlanPdf(plan: ProPlan) {
  const travelmode = TRAVEL_MODE[plan.transport] ?? "walking";
  // Exclusões pedidas pelo utilizador nunca podem reaparecer no PDF Pro.
  const excludedCategories = parseNegativePreferences(
    [(plan as { prompt?: string }).prompt, plan.mood, plan.title].filter(Boolean).join(" "),
  ).excludedCategories;

  const entries: DayPlanEntry[] = [];
  const titles: Record<number, string> = {};
  const dateLabels: Record<number, string> = {};

  for (const day of plan.days) {
    titles[day.day] = `Dia ${day.day} — ${day.city}`;
    const dayDate = (day as unknown as { date?: string }).date;
    if (dayDate) dateLabels[day.day] = dayDate;
    for (const s of day.stops as ProDayStop[]) {
      if (s.kind === "meal" && s.venue) {
        // Restaurante real e validado: entra na numeração e na rota do dia.
        entries.push({
          kind: "stop",
          day: day.day,
          name: s.venue.name,
          lat: s.venue.lat,
          lng: s.venue.lng,
          placeId: s.venue.placeId ?? null,
          blurb: [s.name, s.venue.address].filter(Boolean).join(" · ") || null,
          arrive: s.time || null,
          leave: s.time ? (endTime(s.time, s.durationMin) ?? null) : null,
        });
      } else if (s.kind === "meal") {
        // Pausa genérica: sem nome, morada ou link inventados.
        entries.push({
          kind: "break",
          day: day.day,
          label: s.name,
          start: s.time || null,
          end: s.time ? (endTime(s.time, s.durationMin) ?? null) : null,
          lat: s.lat ?? null,
          lng: s.lng ?? null,
        });
      } else {
        entries.push({
          kind: "stop",
          day: day.day,
          name: s.name,
          lat: s.lat ?? null,
          lng: s.lng ?? null,
          placeId: s.placeId ?? null,
          blurb: s.note ?? null,
          arrive: s.time || null,
          leave: s.time ? (endTime(s.time, s.durationMin) ?? null) : null,
          transitMinutes: s.travelFromPrev?.minutes ?? null,
          transitMeters: s.travelFromPrev?.meters ?? null,
        });
      }
    }
  }

  const sections = buildDaySections({
    entries,
    titles,
    dateLabels,
    excludedCategories,
    expectedDays: plan.days.map((d) => d.day),
  });

  const pdfDays: PdfDaySection[] = [];
  for (const section of sections) {
    const items: PdfItem[] = [];
    // Ordem cronológica: uma refeição num restaurante real fica entre as
    // paragens da manhã e da tarde, nunca no fim do dia.
    for (const node of section.timeline) {
      if (node.kind === "stop") {
        const stop = node.stop;
        items.push({
          type: "stop",
          index: stop.index,
          name: stop.name,
          ...(stop.blurb ? { blurb: stop.blurb } : {}),
          ...(stop.arrive ? { arrive: stop.arrive } : {}),
          ...(stop.leave ? { leave: stop.leave } : {}),
          ...(mapsUrlFor(stop) ? { mapsUrl: mapsUrlFor(stop) } : {}),
          ...(stop.mapped ? { lat: stop.lat as number, lng: stop.lng as number } : {}),
        });
      } else {
        const brk = node.brk;
        items.push({
          type: "break",
          label: brk.label,
          ...(brk.start ? { start: brk.start } : {}),
          ...(brk.end ? { end: brk.end } : {}),
        });
      }
    }

    let mapImageDataUrl: string | undefined;
    if (section.mapPoints.length >= 2) {
      try {
        const map = await getStaticRouteMapDataUrl({
          data: {
            stops: section.mapPoints.slice(0, 25),
            ...(section.venuePoints.length ? { venues: section.venuePoints.slice(0, 20) } : {}),
          },
        });
        mapImageDataUrl = map.dataUrl ?? undefined;
      } catch {
        mapImageDataUrl = undefined;
      }
    }

    const summaryBits = [
      formatDistance(section.totalDistanceMeters) &&
        `Distância: ${formatDistance(section.totalDistanceMeters)}`,
      formatDuration(section.totalTravelMinutes) &&
        `Deslocação: ${formatDuration(section.totalTravelMinutes)}`,
      `${section.stops.length} paragem(ns)`,
    ]
      .filter(Boolean)
      .join("  •  ");

    pdfDays.push({
      title: section.title,
      ...(section.dateLabel ? { dateLabel: section.dateLabel } : {}),
      ...(mapImageDataUrl ? { mapImageDataUrl } : {}),
      ...(dayRouteUrl(section.mapPoints, travelmode)
        ? { routeUrl: dayRouteUrl(section.mapPoints, travelmode) }
        : {}),
      summary: summaryBits,
      ...(section.unmappedNames.length ? { unmappedNames: section.unmappedNames } : {}),
      items,
    });
  }

  const mobility = await buildMobilitySection(plan, "pro");
  const cities = Array.from(new Set(plan.days.map((d) => d.city)));
  await exportItineraryPdf({
    city: cities.join(" · ") || "Roteiro Pro",
    dateLabel: new Date().toLocaleDateString("pt-PT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }),
    modeLabel: TRANSPORT_LABEL[plan.transport] ?? plan.transport,
    items: [],
    days: pdfDays,
    ...(mobility
      ? {
          resources: {
            title: mobility.title,
            ...(mobility.note ? { note: mobility.note } : {}),
            entries: mobility.entries.filter((e) => e.url || e.note),
          },
        }
      : {}),

    footer: "Routify Pro · routify-tour.com",
  });
}
